import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Register: React.FC = () => {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  // Password validation function (same as backend)
  const validatePassword = (password: string) => {
    const errors: string[] = [];

    if (!password) {
      return { isValid: false, errors: ['Password is required'] };
    }

    if (password.length < 12) {
      errors.push('At least 12 characters long');
    }

    if (!/\d/.test(password)) {
      errors.push('At least one number');
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('At least one special character');
    }

    return { isValid: errors.length === 0, errors };
  };

  const handlePasswordChange = (password: string) => {
    setFormData({ ...formData, password });
    const validation = validatePassword(password);
    setPasswordErrors(validation.errors);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const ok = await register(formData.email, formData.name, formData.password);
    setLoading(false);
    setSuccess(ok);
  };

  useEffect(() => {
    if (success && user) {
      navigate('/app/dashboard');
    }
  }, [success, user, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-super-dark-purple to-dusky-purple flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
        <div className="w-40 h-28 mx-auto mb-4 flex items-center justify-center">
            <img
              src="/images/jarvis_logo.png"
              alt="Jarvis Logo"
              className="h-28 w-40 object-contain"
              style={{ display: 'block' }}
            />
          </div>
          <h2 className="text-3xl font-bold text-white">JARVIS RCSA Platform</h2>
          <p className="mt-2 text-gray-300">AI-Powered Risk & Control Assessment</p>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8">
          {success ? (
            <div className="text-green-600 text-center mb-4">Registration successful!</div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-electric-blue focus:border-electric-blue"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-electric-blue focus:border-electric-blue"
                  required
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={e => handlePasswordChange(e.target.value)}
                  onFocus={() => setShowPasswordRequirements(true)}
                  className={`mt-1 block w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-electric-blue focus:border-electric-blue ${
                    passwordErrors.length > 0 && formData.password ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />

                {/* Password Requirements */}
                {(showPasswordRequirements || formData.password) && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
                    <p className="text-xs font-medium text-gray-700 mb-2">Password must contain:</p>
                    <div className="grid grid-cols-1 gap-1 text-xs">
                      <div className={`flex items-center ${formData.password.length >= 12 ? 'text-green-600' : 'text-red-500'}`}>
                        <span className="mr-1">{formData.password.length >= 12 ? '✓' : '✗'}</span>
                        At least 12 characters long
                      </div>
                      <div className={`flex items-center ${/\d/.test(formData.password) ? 'text-green-600' : 'text-red-500'}`}>
                        <span className="mr-1">{/\d/.test(formData.password) ? '✓' : '✗'}</span>
                        At least one number (0-9)
                      </div>
                      <div className={`flex items-center ${/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'text-green-600' : 'text-red-500'}`}>
                        <span className="mr-1">{/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? '✓' : '✗'}</span>
                        At least one special character (!@#$%^&*...)
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <button
                type="submit"
                disabled={loading || passwordErrors.length > 0}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-electric-blue hover:bg-darker-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-electric-blue disabled:opacity-50"
              >
                {loading ? 'Registering...' : 'Register'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-electric-blue hover:text-darker-blue">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register; 